import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Checkbox, Chip, Icon, Skeleton, Text, TextArea } from '@/nova/components';
import { Sheet } from '@/nova/components/Sheet';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { useAppDispatch, useAppSelector } from '@/store';
import { cancelJob, enqueueGeneration, selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { MASCOT_AUTO, type MascotPick } from '../../../art-style/art-style-api';
import { IngredientSection } from '../../../art-style/components/IngredientSection';
import { copyReadyIn, defaultAdCta } from '../../ad-language';
import {
  brandHost,
  kitCopyFromSubject,
  promotionOfferTitle,
  promotionsForTracking,
  type GameLogos,
} from '../../ad-subjects';
import { isLocked, versionKey } from '../../ad-versions';
import { fetchBannerKitCatalog, type BannerKitCatalog, type Palette } from '../../banner-kit-api';
import { EMPTY_PALETTE, applyBrandTheme, brandMatchesTheme } from '../../brand-theme-api';
import type { CreativeItem } from '../../creative-api';
import { assetFilename, downloadAsset } from '../../download-asset';
import { useAdVersions, type MintedVersion } from '../../hooks/useAdVersions';
import { useBrandTheme } from '../../hooks/useBrandTheme';
import { useLocalizedCopy } from '../../hooks/useLocalizedCopy';
import { useSiteLanguages } from '../../hooks/useSiteLanguages';
import { HTML5_BANNER_PROFILE, html5BannerFill, html5BannerSubject, withHtml5BannerCopy } from '../../html5-banners';
import { miniGameFill, miniGameSubject } from '../../mini-game-copy';
import {
  bannerTemplates,
  embedPlayable,
  exportPlayable,
  fetchPlayableTemplates,
  fillPlayableConfig,
  gameTemplates,
  jsonEqual,
  planPlayableRun,
  playableArtChanged,
  playableArtOf,
  playableForkEntry,
  playableHasArt,
  playableOpsCostFor,
  playableVersionGenerated,
  previewPlayable,
  templateFeaturesCharacter,
  upsertPlayable,
  type JsonObject,
  type Playable,
  type PlayableConfigFill,
  type PlayableCopy,
  type PlayableOffer,
  type PlayableParams,
  type PlayablePlateQAReason,
  type PlayableTemplate,
  type PlayableTemplatesResponse,
  type PlayableUpsert,
  type PlayableVersion,
} from '../../playable-api';
import { readStudioColours, withStudioColours, type StudioColours } from '../../studio-preview-colours';
import { useThemeMode } from '@/nova/theme/OwsTheme';
import { buildPlayablePreviewDoc, withDemoCta, withPreviewMode } from '../../playable-preview';
import { isCancelled } from '../../storyboard';
import { subjectCta } from '../../video-subjects';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { promotionName, withGameLogos, withLockedMiniGame, withPromotionSubject } from '../../welcome-bonus';
import { AdLanguageSection } from '../AdLanguageSection';
import {
  AdArtStyleSection,
  AdCharacterSection,
  AdDrawnWith,
  adStyleChoiceOf,
  useAdStyleRun,
  type AdStyleChoice,
} from '../AdArtStyle';
import { LegalLineField, legalPresetLabel } from '../BannerKitComposer/LegalLineField';
import { FooterActions, FooterRow, SelCount } from '../GamePickerSheet/GamePickerSheet.styles';
import { GenerateButton } from '../GenerateButton';
import { ComposerRailTabs, useRailEnter, type ComposerRailTab } from '../ComposerRailTabs';
import { LumioGlyph } from '../LumioMark';
import { PoolPickerSheet } from '../PoolPickerSheet';
import { TrackingLink, type TrackingLinkState } from '../TrackingLink';
import { VersionPicker, type VersionPickerItem } from '../VersionPicker';
import { AssetStatsSheet, StatsPill } from '../AssetStats';
import { BannerRail, formatLabel, type RailSection } from './BannerRail';
import { PlayableLinksSection } from './PlayableLinksSection';
import { BannerSizesStage, bannerSizesMinWidth, type BannerSizeDoc } from './BannerSizesStage';
import { EASY_SPLIT_BESIDE_RAIL_PX } from '../StoryboardComposer/StoryboardComposer.styles';
import { EmbedPanel, type PublishedEmbed } from './EmbedPanel';
import { ExportMenu, ExportOutcome, type PlayableExportOutcome } from './ExportMenu';
import { PreviewFrame, fitScale, formatFrameSize } from './PreviewFrame';
import { SchemaForm } from './SchemaForm';
import { withoutFreeText } from './schema-fields';
import { BrandThemeFields } from './BrandThemeFields';
import { TemplatePicker, type PreviewState } from './TemplatePicker';
import * as S from './PlayableComposer.styles';

export interface PlayableComposerProps {
  tenantId?: number;
  /** The mini-game being edited (the library's live entry: its versions fill in as an art run lands). */
  playable: Playable;
  brandName: string;
  brandDomain?: string;
  /** A mini-game's brand logos as they are now (the primary logo and the bare symbol): applied wherever the document
   *  is used, like its locked words (`withGameLogos`). HTML5 banners keep their own logo. */
  gameLogos?: GameLogos;
  /** The brand's promotion pool (the promotion picker's choices; the tracking link resolves a promotion's on-site
   *  landing path from it). */
  items: CreativeItem[];
  /** Optimistically merge fields into the library's cached mini-game (a card and a reopen read them at once). */
  patch: (id: string, fields: Partial<Playable>) => void;
  onClose: () => void;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
  /** What is edited: a mini-game (default) or an HTML5 banner (a `banner` template: a style over four display
   *  sizes, the copy as live text, a background per size and the subject cut out). */
  kind?: 'game' | 'banner';
}

/** How long an edit rests before it is saved (one write per burst of typing). Not an animation timing. */
const SAVE_DEBOUNCE_MS = 700;
/** How long an edit rests before the live preview is rebuilt (the preview is a long server route). */
const PREVIEW_DEBOUNCE_MS = 800;
/** How long an edit rests before the in-browser preview document replaces the one on screen. */
const LOCAL_PREVIEW_REST_MS = 250;
/** The format the template cards preview in (every template offers it). */
const PICKER_FORMAT = '320x480';
/** The format the HTML5 banner style cards preview in (the medium rectangle, every style's default size). */
const BANNER_PICKER_FORMAT = '300x250';
/** A style card's preview box. */
const BANNER_CARD_W = 240;
const BANNER_CARD_H = 200;
/** The formats a new mini-game (or a template switch with no overlap) starts with, where the template offers them. */
const DEFAULT_FORMATS = ['300x600', '320x480', 'responsive'];
/** The largest box the live preview is shrunk into. */
const STAGE_MAX_W = 480;
const STAGE_MAX_H = 560;

/** The edits waiting for the debounced save: the document (with the draft it was made on, `at`) and the name. */
type PendingPlayableSave = { params?: PlayableParams; name?: string; at?: string };

const isActiveJob = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** The art run to reflect: the one this session started, else the mini-game's linked job, else an art run still in
 *  flight that the link missed (the backend tags it with the mini-game id as its root). Pure. */
export const playableRunJob = (
  playableId: string,
  jobId: string | undefined,
  jobsById: Record<string, CreativeJob>,
): CreativeJob | undefined => {
  const linked = jobId ? jobsById[jobId] : undefined;
  if (linked) return linked;
  return Object.values(jobsById).find(
    (j) => j.kind === 'playable_assets' && j.root_banner_id === playableId && isActiveJob(j),
  );
};

/** An owner-readable reason an art run could not start (the backend's refusal text, matched loosely). Pure. */
export const playableStartErrorText = (err: unknown, t: TFunction, kind: 'game' | 'banner' = 'game'): string => {
  const raw =
    typeof err === 'object' && err !== null && 'error' in err ? String((err as { error: unknown }).error) : '';
  const text = raw.toLowerCase();
  if (text.includes('template')) {
    return kind === 'banner'
      ? t('Choose a style that fits this subject and its sizes.')
      : t('Choose a game that fits this subject and its formats.');
  }
  if (text.includes('not priced')) {
    return kind === 'banner'
      ? t('HTML5 banner art is not available in this environment yet.')
      : t('Mini-game art is not available in this environment yet.');
  }
  if (text.includes('already rendered')) return t('That version already has its art. Edit it to start a new version.');
  return t("That couldn't be started. Please try again.");
};

/** The formats a template accepts out of `wanted`; when none fits, its share of the default set (or its first). */
export const fitFormats = (wanted: readonly string[], template: PlayableTemplate): string[] => {
  const kept = wanted.filter((f) => template.formats.includes(f));
  if (kept.length > 0) return kept;
  const defaults = DEFAULT_FORMATS.filter((f) => template.formats.includes(f));
  return defaults.length > 0 ? defaults : template.formats.slice(0, 1);
};

/** The document with `template` chosen: its schema version, its own settings (kept when it is the same template,
 *  or a switch between two HTML5 banner styles, which share one schema: the offer line, loops and frame time; `from`
 *  is the template chosen until now) and the formats it accepts. Pure. */
export const withTemplate = (
  doc: PlayableParams,
  template: PlayableTemplate,
  from?: PlayableTemplate,
): PlayableParams => ({
  ...doc,
  template_id: template.id,
  schema_version: template.schema_version,
  config:
    doc.template_id === template.id || (from?.category === 'banner' && template.category === 'banner')
      ? doc.config
      : {},
  formats: fitFormats(doc.formats, template),
});

/** A Lumio config fill merged into the document: the suggested template only when none is chosen, the settings
 *  over the current ones, each copy line it wrote, the palette (a blank colour keeps the current one), the site's
 *  CTA button and the site's fonts. Pure. */
export const applyConfigFill = (
  doc: PlayableParams,
  fill: PlayableConfigFill,
  templates: readonly PlayableTemplate[],
): PlayableParams => {
  let next = doc;
  const suggested = !doc.template_id && fill.template_id ? templates.find((t) => t.id === fill.template_id) : undefined;
  if (suggested) next = withTemplate(next, suggested);
  if (fill.config) next = { ...next, config: { ...next.config, ...fill.config } };
  if (fill.copy) {
    const lines = Object.fromEntries(
      Object.entries(fill.copy).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim() !== '',
      ),
    ) as Partial<PlayableCopy>;
    next = { ...next, copy: { ...next.copy, ...lines } };
  }
  if (fill.palette) {
    const cur = next.brand.palette;
    const p = fill.palette;
    next = {
      ...next,
      brand: {
        ...next.brand,
        palette: {
          primary: p.primary || cur?.primary || '',
          accent: p.accent || cur?.accent || '',
          bg: p.bg || cur?.bg || '',
          text: p.text || cur?.text || '',
        },
      },
    };
  }
  if (fill.button) next = { ...next, brand: { ...next.brand, button: { ...fill.button } } };
  if (fill.fonts) {
    next = {
      ...next,
      brand: {
        ...next.brand,
        ...(fill.fonts.heading ? { font_heading: fill.fonts.heading } : {}),
        ...(fill.fonts.body ? { font_body: fill.fonts.body } : {}),
      },
    };
  }
  return next;
};

/** The background's "Needs review" tooltip: what the automatic check still found after its redraws (literal `t()` per
 *  reason, so each is extracted). A background checked before reasons were recorded gets the general line. */
export const plateReviewReason = (reason: PlayablePlateQAReason | undefined, t: TFunction): string => {
  switch (reason) {
    case 'middle_busy':
      return t('Something sits where the game is played on this background. Look it over, or generate again.');
    case 'text':
      return t('This background may carry lettering or a sign. Look it over, or generate again.');
    case 'money_objects':
      return t('This background may show coins, chips or cards. Look it over, or generate again.');
    default:
      return t('The automatic check was not sure about this background. Look it over, or generate again.');
  }
};

const cloneParams = (p: PlayableParams): PlayableParams => JSON.parse(JSON.stringify(p)) as PlayableParams;

/**
 * The mini-game and HTML5 banner composer, framed like the Image Banners composer. Header: the name and the version
 * picker. A new mini-game first picks its game (cards playing each template's demo). Then, left, the Art Style page's
 * ingredient sections, one open at a time: the subject with its locked copy (a mini-game's offer, call to action and
 * game text are fixed: the promotion's own and the game's vetted lines; an HTML5 banner's headline, offer line and
 * call to action are its subject's own), the ad's language (one of the website's: every word the ad shows is in
 * it, the subject's copy in it filled in when it arrives), the game or style and "Fill with Lumio", the template's
 * settings, the formats or sizes, the brand's art style and character, the art direction, the brand colours, the
 * legal line (a mini-game's always on, its market's preset) and the links; Generate art (with its credit cost) pinned
 * under them, Stop while running. Right: the run's notices, the live preview (every size as a card for an HTML5
 * banner), the art of the selected version, the last export, the hosted embed and the tracking link; the export pinned
 * under them once the version has its art.
 *
 * Versions hold the AI art (the background plate and the sprite sheets). A run LOCKS its version; editing a locked
 * version forks a new one that keeps the art while the edit does not reach it (copy, offer, settings are drawn
 * live by the runtime), or starts without art when it does (template, colours, art direction). The preview always
 * shows the document on screen over the selected version's art. The art run renders the SAVED document, so a
 * Generate writes it first.
 */
export const PlayableComposer = ({
  tenantId,
  playable,
  brandName,
  brandDomain,
  gameLogos,
  items,
  patch,
  onClose,
  onBuyCredits,
  onNotify,
  kind = 'game',
}: PlayableComposerProps): React.ReactElement => {
  const { t } = useTranslation();
  const isBanner = kind === 'banner';
  // The raw generation controls (Lumio's fill, a game's settings, the motion, the brand colours), the legal line and
  // the click-through show only with the "777devdebug" Lumio code (the creatives dev flag): an owner's rail is the
  // subject, the game or style, the formats, the art style and character, and the art direction.
  const gate = useFeatureGate();
  const devDebug = gate.isEnabled('creatives-dev-debug');
  // On a phone the two rails are tabs (Ingredients / Banners or Mini-games); Generate from Ingredients shows the art.
  const [railTab, setRailTab] = React.useState<ComposerRailTab>('ingredients');
  const railEnter = useRailEnter(railTab);
  // The split's width: an HTML5 banner's sizes need room beside the rail (see `tabbed`). Measured off the split, which
  // spans the sheet in both layouts, so going tabbed never changes the reading that chose it.
  const [splitEl, setSplitEl] = React.useState<HTMLDivElement | null>(null);
  const [splitW, setSplitW] = React.useState(0);
  // Syncs with the layout (the split's width), before paint so a narrow sheet never shows a clipped first frame.
  React.useLayoutEffect(() => {
    if (!splitEl) return undefined;
    const read = (): void => setSplitW(splitEl.clientWidth);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(splitEl);
    return () => observer.disconnect();
  }, [splitEl]);
  const pickerFormat = isBanner ? BANNER_PICKER_FORMAT : PICKER_FORMAT;
  const dispatch = useAppDispatch();
  const pricing = useAppSelector((state) => state.aiCredits?.pricing);
  const jobsById = useAppSelector(selectCreativeJobsMap);

  /* ---------- the template registry and the legal presets (static per deploy, cached by the clients) ---------- */

  const [registry, setRegistry] = React.useState<PlayableTemplatesResponse | null>(null);
  const [registryFailed, setRegistryFailed] = React.useState(false);
  const [catalog, setCatalog] = React.useState<BannerKitCatalog | null>(null);
  React.useEffect(() => {
    let live = true;
    void fetchPlayableTemplates().then((r) => {
      if (!live) return;
      if (r) setRegistry(r);
      else setRegistryFailed(true);
    });
    void fetchBannerKitCatalog().then((c) => {
      if (live && c) setCatalog(c);
    });
    return () => {
      live = false;
    };
  }, []);
  // With the built runtime in the registry, previews are assembled in the browser (live, no server round trip);
  // otherwise they come from the backend's preview route when it can render them.
  const runtime = registry?.runtime ?? null;
  const canPreview = runtime !== null || registry?.features.html5 === true;

  /* ---------- saving: one ordered write chain, edits debounced ---------- */

  const writeChainRef = React.useRef<Promise<unknown>>(Promise.resolve());
  const pendingRef = React.useRef<PendingPlayableSave>({});
  const saveTimerRef = React.useRef<number | null>(null);
  const notifyRef = React.useRef(onNotify);
  notifyRef.current = onNotify;

  // Whether a version is saved (set once the versions hook is up, below): a write naming a draft waits for it.
  const persistedOfRef = React.useRef<(key: string) => Promise<boolean>>(() => Promise.resolve(true));

  /** Queue one upsert after every earlier one (a slow write never lands over a newer one). A write naming a draft
   *  (ad_version_at, no new version) waits for that draft's own save: one the backend does not have (yet, or ever)
   *  would take nothing, so the document then goes to the mini-game alone. */
  // The brand this entity belongs to, as the composer opened it: a brand switch re-renders the composer once with
  // the new brand before it is closed, and its last save must still go to the entity's own brand.
  const entityTenantRef = React.useRef(tenantId);
  if (entityTenantRef.current === undefined) entityTenantRef.current = tenantId;
  const write = React.useCallback(
    (body: Omit<PlayableUpsert, 'id'>): Promise<boolean> => {
      const tenant = entityTenantRef.current;
      if (!tenant) return Promise.resolve(false);
      const run = writeChainRef.current
        .then(async () => {
          let send = body;
          const draftKey = send.ad_version_at && !send.ad_version ? versionKey(send.ad_version_at) : undefined;
          if (draftKey && !(await persistedOfRef.current(draftKey))) {
            const { ad_version_at: _drop, ...rest } = send;
            send = rest;
          }
          await upsertPlayable(tenant, { id: playable.id, ...send });
        })
        .then(
          () => true,
          () => false,
        );
      writeChainRef.current = run;
      return run;
    },
    [playable.id],
  );

  /** Take the debounced edits out of the queue (a caller writes them itself, or drops them). */
  const takePending = React.useCallback((): PendingPlayableSave => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = {};
    return pending;
  }, []);

  const flush = React.useCallback((): Promise<boolean> => {
    const { params: p, name: n, at } = takePending();
    const body: Omit<PlayableUpsert, 'id'> = {
      // A draft's edits are saved on the draft too (it owns its document).
      ...(p ? { ad_params: p, ...(at ? { ad_version_at: at } : {}) } : {}),
      ...(n && n.trim() ? { name: n.trim() } : {}),
    };
    if (!body.ad_params && !body.name) return Promise.resolve(true);
    return write(body).then((ok) => {
      if (!ok) notifyRef.current(t("Your changes couldn't be saved. Please try again."));
      return ok;
    });
  }, [takePending, write, t]);

  const schedule = React.useCallback(
    (fields: PendingPlayableSave): void => {
      // An edit on another draft than the one waiting: the waiting edits are written to their own draft first.
      const waiting = pendingRef.current;
      if (fields.params !== undefined && waiting.params !== undefined && waiting.at !== fields.at) void flush();
      pendingRef.current = { ...pendingRef.current, ...fields };
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(() => void flush(), SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  // Closing the composer (or a brand switch unmounting it) never drops an edit still resting in the debounce.
  const flushRef = React.useRef(flush);
  flushRef.current = flush;
  React.useEffect(
    () => () => {
      void flushRef.current();
    },
    [],
  );

  /* ---------- the document and its versions ---------- */

  // The mini-game's first version could not be saved (taken back): previews have nothing to read.
  const [noVersion, setNoVersion] = React.useState(false);

  const [name, setName] = React.useState(playable.name);
  const nameRef = React.useRef<HTMLInputElement>(null);
  // The game's (banner's) tracking-link performance (the header's Stats pill).
  // The stats sheet is the address's (`?stats=<id>`): Back from one of its deep links reopens it.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsOpen = statsSheet.value === playable.id;
  // The editor opens on the newest version, on its own document (a draft owns its document; the mini-game's working
  // document is only what a run reads).
  const [params, setParams] = React.useState<PlayableParams>(
    () => playable.ad_versions[0]?.params ?? playable.ad_params,
  );
  const paramsRef = React.useRef(params);
  // The words the ad shows are never the owner's: an HTML5 banner's copy is its subject's own; a mini-game's offer and
  // call to action are its promotion's, its game text the game's fixed lines and its legal line its market's preset.
  // Derived wherever the document is used (previews, the run, the package), never patched into it on open: an edit on
  // a generated version forks it, so a patch on open would fork every ad reopened. A mini-game's owner actions that
  // change what the text follows (another game, another promotion, a Lumio fill) write the locked text with them.
  // A mini-game is drawn with the brand's logos as they are now, the same way (never patched in on open).
  const lockCopy = React.useCallback(
    (doc: PlayableParams): PlayableParams =>
      isBanner ? withHtml5BannerCopy(doc) : withGameLogos(withLockedMiniGame(doc), gameLogos),
    [isBanner, gameLogos],
  );
  const lockGame = React.useCallback(
    (doc: PlayableParams): PlayableParams => (isBanner ? doc : withLockedMiniGame(doc)),
    [isBanner],
  );
  const lockedParams = React.useMemo(() => lockCopy(params), [lockCopy, params]);

  const { versions, selected, select, current, fork, forkIfLocked, mintTarget, patchVersion, persistedOf } =
    useAdVersions<PlayableVersion>({
      versions: playable.ad_versions,
      entityId: playable.id,
      tenantId,
      persistFork: (entry, at) =>
        tenantId
          ? upsertPlayable(tenantId, { id: playable.id, ad_version: entry, ad_version_at: at })
          : Promise.resolve(''),
      patchEntity: (next) => patch(playable.id, { ad_versions: next }),
      onForkLost: (restored, noneLeft) => {
        notifyRef.current(t("Your changes couldn't be saved. Please try again."));
        // The editor shows the restored version's document again (the lost fork's edits are gone with it).
        if (restored) {
          paramsRef.current = restored.params;
          setParams(restored.params);
          // The lost fork's edits waiting in the debounce go with it (a name keeps waiting).
          const { name: waitingName } = takePending();
          if (waitingName !== undefined) schedule({ name: waitingName });
        }
        // A mini-game whose first version could not be saved has nothing to preview: say so, not "loading".
        if (noneLeft) setNoVersion(true);
      },
    });
  // A preview or an export of a version minted this session waits until the backend has it (persistedOf).
  persistedOfRef.current = persistedOf;

  const templateOf = React.useCallback(
    (id: string): PlayableTemplate | undefined => (id ? registry?.templates.find((tpl) => tpl.id === id) : undefined),
    [registry],
  );
  const template = templateOf(params.template_id);
  // Only an icon or symbol sheet draws the art style's character (one of its items): the picker shows for those games.
  const featuresCharacter = templateFeaturesCharacter(template);
  // The games on offer: the registry's `game` templates (the `banner` ones are HTML5 Banners), those fitting the
  // subject first (all games when none fits). An HTML5 banner offers the `banner` styles the same way.
  const games = React.useMemo(
    () => (isBanner ? bannerTemplates : gameTemplates)(registry?.templates ?? []),
    [registry, isBanner],
  );
  const candidates = React.useMemo(() => {
    const fit = games.filter((tpl) => tpl.subjects.includes(params.subject_type));
    return fit.length > 0 ? fit : games;
  }, [games, params.subject_type]);

  /* ---------- the run ---------- */

  const [sessionJobId, setSessionJobId] = React.useState<string | undefined>(undefined);
  const job = playableRunJob(playable.id, sessionJobId ?? playable.job_id, jobsById);
  const active = isActiveJob(job);
  const [runTargetKey, setRunTargetKey] = React.useState<string | undefined>(undefined);
  const runKey = active
    ? runTargetKey && versions.some((v) => v.key === runTargetKey && !isLocked(v))
      ? runTargetKey
      : versions.find((v) => !isLocked(v))?.key
    : undefined;

  const [submitting, setSubmitting] = React.useState(false);
  const [stopping, setStopping] = React.useState(false);
  const [filling, setFilling] = React.useState(false);
  // No edits while an art run is starting or in flight: the run paints the document saved at its start.
  const formLocked = active || submitting;
  // The rail's inputs also wait while a Lumio fill is on its way: it lands on the document it was asked for.
  const railLocked = formLocked || filling;

  /* ---------- edits ---------- */

  // Which art style the next run draws the backgrounds and sprites in (the brand's primary unless the owner picks
  // another) and which of that style's characters ('' auto, 'none' or a mascot id). Not saved in the document: both
  // start from how the newest version was drawn, and ride each Generate as `style_id` and `mascot_id`.
  const styleRun = useAdStyleRun(tenantId, playable.ad_versions[0]?.art_style);
  const mascotPick = styleRun.mascotPick;
  const runStyleId = styleRun.styleId;
  // Only a read style can say a version was drawn in another one (while it is read, an edit keeps the art as before).
  const styleKnown = styleRun.ready;

  /** Apply an edit to the document (see patchParams for the guard): fork a version it may not change, land it on the
   *  draft it is made on, and save it there and on the mini-game. */
  const applyEdit = React.useCallback(
    (next: PlayableParams): void => {
      const sel = current();
      if (sel && isLocked(sel)) {
        // A character picked since this version was drawn, for a game whose sheets feature it: its art holds the old
        // one, so the fork carries none and the next run draws the pick.
        // Like the style, the character pick counts only once the styles are known: while they load (or after both
        // reads failed) the pick reads Auto, which is not a change of the drawn one.
        const characterChanged =
          styleKnown &&
          templateFeaturesCharacter(templateOf(next.template_id)) &&
          (sel.art_style?.mascot_pick ?? MASCOT_AUTO) !== mascotPick;
        // Likewise a style picked since: its art holds the old look.
        const styleChanged = styleKnown && (sel.art_style?.style_id ?? '') !== runStyleId;
        forkIfLocked(
          characterChanged || styleChanged ? { params: cloneParams(next), assets: {} } : playableForkEntry(sel, next),
        );
      } else if (sel && playableHasArt(sel.assets) && playableArtChanged(sel.params, next)) {
        // An unlocked version whose art came from another template / colours / art direction: its art cannot be
        // cleared, so the edit moves to a new version with none.
        fork({ params: cloneParams(next), assets: {} });
      }
      // The edit lands on the draft it was made on (an art-affecting edit never reaches a draft whose art it would
      // contradict: it forked above), and is saved there with the mini-game's working document.
      const draft = current();
      const onDraft = draft && !isLocked(draft) ? draft : undefined;
      if (onDraft) patchVersion(onDraft.key, next);
      paramsRef.current = next;
      setParams(next);
      patch(playable.id, { ad_params: next });
      schedule({ params: next, at: onDraft?.created_at });
    },
    [
      current,
      mascotPick,
      styleKnown,
      runStyleId,
      templateOf,
      forkIfLocked,
      fork,
      patchVersion,
      patch,
      playable.id,
      schedule,
    ],
  );
  /** An owner's edit: none while a run starts or paints, nor while a Lumio fill is on its way (it lands on the
   *  document it was asked for). */
  const patchParams = React.useCallback(
    (next: PlayableParams): void => {
      if (formLocked || filling) return;
      applyEdit(next);
    },
    [formLocked, filling, applyEdit],
  );

  const setOffer = (fields: Partial<PlayableOffer>): void =>
    patchParams({ ...paramsRef.current, offer: { ...paramsRef.current.offer, ...fields } });
  const setPalette = (key: keyof Palette, value: string): void => {
    const cur = paramsRef.current;
    patchParams({
      ...cur,
      brand: { ...cur.brand, palette: { ...(cur.brand.palette ?? EMPTY_PALETTE), [key]: value } },
    });
  };
  const setConfig = (config: JsonObject): void => patchParams({ ...paramsRef.current, config });
  const toggleFormat = (format: string, on: boolean): void => {
    const cur = paramsRef.current;
    const next = on ? [...cur.formats.filter((f) => f !== format), format] : cur.formats.filter((f) => f !== format);
    if (next.length === 0) return; // a mini-game always has at least one format
    // Keep the template's own order, so the preview switcher and the export read the same way every time.
    const order = template?.formats ?? next;
    patchParams({ ...cur, formats: order.filter((f) => next.includes(f)) });
  };

  const onName = (value: string): void => {
    setName(value);
    if (!value.trim()) return; // a blank name is "not provided": keep the saved one
    patch(playable.id, { name: value.trim() });
    schedule({ name: value });
  };

  // The style and character each version was left on this session (what its next run draws), so going back restores
  // them.
  const pickByKeyRef = React.useRef<Record<string, AdStyleChoice>>({});
  const onSelectVersion = (key: string): void => {
    const v = versions.find((x) => x.key === key);
    // Not while a fill, an export or an embed is on its way: each is for the version it was asked on.
    if (!v || filling || exporting || embedding) return;
    const left = current();
    if (left) pickByKeyRef.current[left.key] = styleRun.choice;
    void flush();
    select(key);
    // Show that version's document (viewing writes nothing; an edit forks it when it is locked).
    const doc = v.params;
    paramsRef.current = doc;
    setParams(doc);
    // Its style and character: the ones it was left on, else the ones its art was drawn with (an edit that keeps the
    // art is then no style or character change), else the current picks (no art yet).
    const kept = pickByKeyRef.current[key];
    if (kept !== undefined) styleRun.setChoice(kept);
    else if (playableHasArt(v.assets)) styleRun.setChoice(adStyleChoiceOf(v.art_style));
  };

  /* ---------- the game ---------- */

  const [pickingTemplate, setPickingTemplate] = React.useState(false);
  // The rail's open ingredient section (one at a time, like the Art Style page): the subject first.
  const [openSection, setOpenSection] = React.useState<RailSection | null>('subject');
  const showPicker = pickingTemplate || !params.template_id;

  const pickTemplate = (id: string): void => {
    const tpl = templateOf(id);
    if (!tpl || formLocked || filling) return;
    const cur = paramsRef.current;
    if (tpl.id !== cur.template_id) patchParams(lockGame(withTemplate(cur, tpl, templateOf(cur.template_id))));
    setPickingTemplate(false);
  };

  const fillWithLumio = async (): Promise<void> => {
    if (!tenantId || formLocked || filling) return;
    setFilling(true);
    // The fill is written for this version and this game: one that lands after the owner moved to another is dropped.
    const askedOn = current()?.key;
    const askedGame = paramsRef.current.template_id;
    try {
      const res = await fillPlayableConfig(tenantId, playable.id, paramsRef.current);
      if (current()?.key !== askedOn || paramsRef.current.template_id !== askedGame) return;
      if (!res.ok) {
        onNotify(
          isBanner
            ? t("Lumio couldn't fill this HTML5 banner right now. Edit it yourself, or try again.")
            : t("Lumio couldn't fill this mini-game right now. Edit it yourself, or try again."),
        );
        return;
      }
      // Lumio writes no copy: an HTML5 banner's headline, offer line and call to action are the subject's own, a
      // mini-game's text is locked (and follows the game the fill may pick).
      applyEdit(
        lockGame(applyConfigFill(paramsRef.current, isBanner ? html5BannerFill(res) : miniGameFill(res), games)),
      );
      if (paramsRef.current.template_id) setPickingTemplate(false);
    } finally {
      setFilling(false);
    }
  };

  /* ---------- the promotion it advertises ---------- */

  // Change opens the brand's promotions (one pick); confirming swaps the subject (a locked version forks).
  const [pickingPromotion, setPickingPromotion] = React.useState(false);
  const [promotionPick, setPromotionPick] = React.useState<string | null>(null);
  const openPromotionPicker = (): void => {
    if (formLocked) return;
    setPromotionPick(paramsRef.current.subject.id || null);
    setPickingPromotion(true);
  };
  const confirmPromotion = (): void => {
    setPickingPromotion(false);
    const item = items.find((i) => i.banner_id === promotionPick);
    if (!item || formLocked || item.banner_id === paramsRef.current.subject.id) return;
    patchParams(withPromotionSubject(paramsRef.current, item, brandDomain));
  };

  /* ---------- the art style's character (a run option, never part of the document) ---------- */

  /** Whether the picked art style differs from the one `sel` was drawn with (its art holds the old look). */
  const styleChangedFor = (sel: PlayableVersion | undefined): boolean =>
    Boolean(sel) && (sel?.art_style?.style_id ?? '') !== runStyleId;
  /** Whether the next run draws a different character than `sel` was drawn with, for a game whose sheets feature it
   *  (its carried art would keep the old character). */
  const characterChangedFor = (
    sel: PlayableVersion | undefined,
    pick: MascotPick,
    tpl: PlayableTemplate | undefined,
  ): boolean => Boolean(sel) && templateFeaturesCharacter(tpl) && (sel?.art_style?.mascot_pick ?? MASCOT_AUTO) !== pick;
  // The picks are run options: art painted in another style or with another character is never filled in around
  // (planPlayableRun starts the next run on a new version, and an edit on a generated version forks it with no art),
  // so picking forks nothing.
  const onMascotPick = (next: MascotPick): void => {
    if (formLocked || filling || next === mascotPick) return;
    styleRun.pickMascot(next);
  };
  const onStylePick = (next: string): void => {
    if (formLocked || filling || next === runStyleId) return;
    styleRun.pickStyle(next);
  };

  /* ---------- the brand theme (the website's palette, CTA button and fonts) ---------- */

  const brandTheme = useBrandTheme(tenantId);
  /** Apply an edit to the document as it is NOW (an edit earlier in the same event included). */
  const editParams = (fn: (cur: PlayableParams) => PlayableParams): void => patchParams(fn(paramsRef.current));
  const resetToBrandTheme = (): void => {
    if (formLocked || !brandTheme) return;
    const cur = paramsRef.current;
    patchParams({ ...cur, brand: applyBrandTheme(cur.brand, brandTheme) });
  };

  /* ---------- the ad's language ---------- */

  // The website's languages (the Language section's options, and the default a document without a language takes)
  // and the subject's copy in the ad's language (the website's own text, else Lumio's translation).
  const siteLanguages = useSiteLanguages(tenantId);
  const localized = useLocalizedCopy({
    tenantId,
    language: params.language,
    subjectType: params.subject_type,
    subject: params.subject,
    current: params.localized_copy,
  });
  // Whether the document holds its subject's copy in its language (always, for an older document with none): until it
  // does, a run or a package would carry the snapshotted copy, so neither starts.
  const copyReady = copyReadyIn(params.localized_copy, params.language, params.subject.id);

  /** An automatic fill (values the owner has no field for: the website's default language, the subject's copy in it),
   *  written onto the selected draft and the working document WITHOUT the edit path's forks: an automatic fill never
   *  forks a draft's art away. Neither value reaches the art (it is text-free), so a draft holding art takes it as
   *  is; a locked version is never written. */
  const autoFill = React.useCallback(
    (fields: Partial<PlayableParams>): void => {
      const sel = current();
      if (sel && isLocked(sel)) return;
      const next = lockGame({ ...paramsRef.current, ...fields });
      if (sel) patchVersion(sel.key, next);
      paramsRef.current = next;
      setParams(next);
      patch(playable.id, { ad_params: next });
      schedule({ params: next, at: sel?.created_at });
    },
    [current, lockGame, patchVersion, patch, playable.id, schedule],
  );
  // An automatic fill lands only on an unlocked version, and never while a run starts or paints or a Lumio fill is on
  // its way (each writes the document it was asked for).
  const fillable = !formLocked && !filling && !(selected && isLocked(selected));
  // The website's default language goes only into a document that holds no art (a new ad whose read had not answered
  // when it was made, an older draft): an older ad with art keeps rendering as it was made until the owner picks one.
  const languageFillable = fillable && !(selected && playableHasArt(selected.assets));
  const siteDefault = siteLanguages.known ? siteLanguages.defaultLanguage : '';
  // A document with no language that will take the website's default is not run before the read answers: a run would
  // lock a version in no language (the read gives up on its own after a few seconds, and a failed read runs as before).
  const awaitingLanguage = languageFillable && !params.language && siteLanguages.status === 'loading';
  // Syncs the document with the website's languages (an async read): a document with no language takes the default.
  React.useEffect(() => {
    if (!languageFillable || !siteDefault || params.language) return;
    autoFill({ language: siteDefault });
  }, [languageFillable, siteDefault, params.language, autoFill]);
  // Syncs the document with the subject's copy in its language (an async read, or built locally for a game): stored
  // once it arrives, so the locked words, the run and the package read it. Stored under the document's own language id
  // (the service may write the id it was asked for differently), so the copy always counts as this language's.
  const arrivedCopy = localized.status === 'ready' ? localized.copy : null;
  React.useEffect(() => {
    if (!fillable || !arrivedCopy) return;
    const language = (params.language ?? '').trim();
    if (!language || copyReadyIn(params.localized_copy, language, arrivedCopy.subject_id)) return;
    const copy = { ...arrivedCopy, language };
    if (params.localized_copy && jsonEqual(params.localized_copy, copy)) return;
    autoFill({ localized_copy: copy });
  }, [fillable, arrivedCopy, params.localized_copy, params.language, autoFill]);

  /** The owner picks the ad's language: an edit like any other (a locked version forks, keeping its art), with a
   *  mini-game's locked text in the new language. */
  const onLanguage = (language: string): void => {
    const cur = paramsRef.current;
    if (language === cur.language) return;
    patchParams(lockGame({ ...cur, language }));
  };

  /* ---------- generate art / stop ---------- */

  const runPlan = template
    ? planPlayableRun(selected, params, template, {
        characterChanged: characterChangedFor(selected, mascotPick, template),
        styleChanged: styleChangedFor(selected),
      })
    : undefined;
  // No price until the art style is read: the plan (what is redrawn) follows it.
  const cost = runPlan && styleRun.ready ? playableOpsCostFor(runPlan.ops, pricing) : undefined;

  const startRun = async (): Promise<void> => {
    const tpl = templateOf(paramsRef.current.template_id);
    // Not while a Lumio fill is in flight: it lands after its await and would rewrite the document the run paints.
    // The run's art reuse and price follow the picked art style: none starts before it is read.
    if (!tenantId || !tpl || active || submitting || filling || !styleRun.ready) return;
    // The run paints the document with its words in the ad's language: not before the subject's copy in it is held.
    const asked = paramsRef.current;
    if (!copyReadyIn(asked.localized_copy, asked.language, asked.subject.id) || awaitingLanguage) return;
    setSubmitting(true);
    try {
      // The locked copy (the subject's, the promotion's offer) is written into the document the run renders, and
      // kept in the editor's document so the next edit compares against it.
      const doc = lockCopy(paramsRef.current);
      paramsRef.current = doc;
      setParams(doc);
      patch(playable.id, { ad_params: doc });
      // The run paints the mini-game's SAVED document: write it now (the debounce may still hold the last edit).
      const { name: pendingName } = takePending();
      // The selected draft takes the document too (it owns it), so a run that never starts leaves it current.
      const editing = current();
      const draft = editing && !isLocked(editing) ? editing : undefined;
      if (draft) patchVersion(draft.key, doc);
      const saved = await write({
        ad_params: doc,
        ...(draft ? { ad_version_at: draft.created_at } : {}),
        ...(pendingName?.trim() ? { name: pendingName.trim() } : {}),
      });
      // A fork lost while it saved moved the selection back (onForkLost told the owner): nothing runs on another
      // version than the one the owner pressed Generate on.
      if (current()?.key !== editing?.key) return;
      if (!saved) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return;
      }
      const plan = planPlayableRun(current(), doc, tpl, {
        characterChanged: characterChangedFor(current(), mascotPick, tpl),
        styleChanged: styleChangedFor(current()),
      });
      const target = plan.reuse ? mintTarget({ params: cloneParams(doc), assets: {} }) : fork(plan.entry);
      if (!target) {
        onNotify(t("That couldn't be started. Please try again."));
        return;
      }
      // The run reads the version the backend has: a fork (and the art it carries) must be saved first.
      // A fork the backend could not save was taken back (onForkLost told the owner).
      if (!(await target.persisted)) return;
      // The run paints `doc` into its target (the backend writes it there too): the draft shows the document it is
      // drawn from.
      patchVersion(target.key, doc);
      const res = await dispatch(
        enqueueGeneration({
          tenantId,
          placement: 'playable',
          prompt: 'playable assets',
          mediaType: 'playable_assets',
          ad: { playable_id: playable.id, target_version_at: target.at },
          // The art style's character (Auto sends nothing), only for a game whose art features it.
          mascotId: templateFeaturesCharacter(tpl) ? mascotPick || undefined : undefined,
          // The picked art style the art is drawn in.
          styleId: styleRun.sendStyleId,
        }),
      ).unwrap();
      if (!res.ok) {
        if (res.needed !== undefined) onBuyCredits(res.needed);
        else onNotify(t("That couldn't be started. Please try again."));
        return;
      }
      setRunTargetKey(target.key);
      if (res.jobId) {
        // Link the run to the mini-game so a reopen (and the library card) follows its progress.
        setSessionJobId(res.jobId);
        patch(playable.id, { job_id: res.jobId });
        void write({ job_id: res.jobId });
      }
      void dispatch(fetchOwnerWallets());
    } catch (err) {
      onNotify(playableStartErrorText(err, t, kind));
    } finally {
      setSubmitting(false);
    }
  };

  const stop = async (): Promise<void> => {
    if (!tenantId || !job || !active || stopping) return;
    setStopping(true);
    try {
      await dispatch(cancelJob({ tenantId, jobId: job.id })).unwrap();
    } catch {
      onNotify(t("That couldn't be stopped. Please try again."));
    } finally {
      setStopping(false);
    }
  };

  /* ---------- previews ---------- */

  // A preview reads a version's art, so the mini-game needs one: a mini-game with none gets its first (empty)
  // version as soon as previews are possible. Once per composer.
  const ensuredRef = React.useRef(false);
  const hasVersions = versions.length > 0;
  React.useEffect(() => {
    if (ensuredRef.current || hasVersions || !tenantId || !canPreview) return;
    ensuredRef.current = true;
    fork({ params: cloneParams(paramsRef.current), assets: {} });
  }, [hasVersions, tenantId, canPreview, fork]);

  const previewVersionAt = selected?.created_at;
  const previewVersionKey = selected?.key;
  // The art the selected version holds: a landed asset rebuilds the preview.
  const artSignature = React.useMemo(() => JSON.stringify(selected?.assets ?? {}), [selected?.assets]);

  // The cards' demos: one per template, fetched once per composer with this mini-game's copy and colours on the
  // template's own default settings.
  const [cardPreviews, setCardPreviews] = React.useState<Record<string, PreviewState>>({});
  // Requested (in flight or settled) and settled demos: a demo cut short when the picker closes is asked for again.
  const requestedCardsRef = React.useRef(new Set<string>());
  const settledCardsRef = React.useRef(new Set<string>());
  // Built in the browser when the runtime is in the registry: every card at once, redrawn with the copy and art.
  // A mini-game's demos' CTA when it has none of its own yet (or only a "Deposit" call): an acquisition call. An
  // HTML5 banner's demos show its own (locked) call to action.
  // With an ad language, the demos' fallback CTA is the subject type's default in it (ad content, never translated).
  const demoCta = (params.language ?? '').trim()
    ? defaultAdCta(params.subject_type, params.language)
    : params.subject_type === 'promotion'
      ? t('Claim bonus')
      : subjectCta(params.subject_type, t) || t('Claim bonus');
  // A mini-game's picker demos are drawn in the Studio's own colours (its theme and accent as they are now), with no
  // generated art: the art a run paints recolours the finished game, so the brand's colours here would promise a look
  // the game may not have. Read once per theme / accent switch.
  const themeMode = useThemeMode();
  // The theme provider flips `data-ows-theme` / `data-ows-accent` on <html> in an effect AFTER this render, so the
  // colours are read once on mount and again whenever those attributes change (a theme switch, an OS switch under
  // 'auto', the saved appearance adopted late), never during render.
  const [studioColours, setStudioColours] = React.useState<StudioColours | null>(null);
  React.useEffect(() => {
    if (isBanner || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const read = (): void => setStudioColours(readStudioColours());
    read();
    const observer = new MutationObserver(read);
    observer.observe(root, { attributes: true, attributeFilter: ['data-ows-theme', 'data-ows-accent'] });
    return () => observer.disconnect();
  }, [isBanner]);
  const localCardPreviews = React.useMemo<Record<string, PreviewState> | null>(() => {
    if (!runtime || !showPicker) return null;
    const out: Record<string, PreviewState> = {};
    candidates.forEach((tpl) => {
      // The card's one document: a controlled preview resting on the attract loop until the card plays it (a runtime
      // without the control channel ignores the flag and rests on the same untimed loop). The quick playthrough is
      // that runtime's hover document only.
      const styled = lockCopy(withTemplate(params, tpl, template));
      const branded = isBanner ? styled : withDemoCta(styled, demoCta);
      const demo = studioColours ? withStudioColours(branded, studioColours, themeMode) : branded;
      // Studio-coloured demos carry no generated art (its colours are the brand's / the art style's).
      const art = studioColours ? undefined : selected?.assets;
      const html = buildPlayablePreviewDoc(runtime, tpl.id, pickerFormat, withPreviewMode(demo, 'idle'), art, {
        preview: true,
      });
      const playHtml = buildPlayablePreviewDoc(runtime, tpl.id, pickerFormat, withPreviewMode(demo, 'play'), art);
      out[tpl.id] = html ? { status: 'ready', html, playHtml: playHtml ?? undefined } : { status: 'unavailable' };
    });
    return out;
  }, [
    runtime,
    showPicker,
    candidates,
    params,
    template,
    pickerFormat,
    selected?.assets,
    demoCta,
    lockCopy,
    isBanner,
    studioColours,
    themeMode,
  ]);
  React.useEffect(() => {
    if (runtime || !showPicker || !tenantId || !canPreview || !previewVersionAt || !previewVersionKey) return;
    const controller = new AbortController();
    const requested = requestedCardsRef.current;
    const settled = settledCardsRef.current;
    const wanted = candidates.filter((tpl) => !requested.has(tpl.id));
    if (wanted.length === 0) return;
    wanted.forEach((tpl) => requested.add(tpl.id));
    setCardPreviews((cur) => ({ ...cur, ...Object.fromEntries(wanted.map((tpl) => [tpl.id, { status: 'loading' }])) }));
    void persistedOf(previewVersionKey).then((ok) => {
      if (controller.signal.aborted) return;
      wanted.forEach((tpl) => {
        // The mini-game's own copy and colours, on this template's settings (its defaults unless it is the chosen one).
        const demo = lockCopy(withTemplate(paramsRef.current, tpl, templateOf(paramsRef.current.template_id)));
        const done = (state: PreviewState): void => {
          if (controller.signal.aborted) return;
          settled.add(tpl.id);
          setCardPreviews((cur) => ({ ...cur, [tpl.id]: state }));
        };
        if (!ok) {
          done({ status: 'error' });
          return;
        }
        void previewPlayable(
          tenantId,
          playable.id,
          { version_at: previewVersionAt, format: pickerFormat, params: demo },
          controller.signal,
        ).then((res) => {
          if (res.ok) done({ status: 'ready', html: res.data.html });
          else if (!res.aborted) done({ status: res.status === 409 ? 'unavailable' : 'error' });
        });
      });
    });
    return () => {
      controller.abort();
      wanted.forEach((tpl) => {
        if (!settled.has(tpl.id)) requested.delete(tpl.id);
      });
    };
  }, [
    runtime,
    showPicker,
    tenantId,
    canPreview,
    previewVersionAt,
    previewVersionKey,
    candidates,
    persistedOf,
    playable.id,
    pickerFormat,
    templateOf,
    lockCopy,
  ]);

  // The live preview: the document on screen over the selected version's art, in one format, rebuilt shortly after
  // each edit (and when art lands).
  const [previewFormatPick, setPreviewFormatPick] = React.useState<string>('');
  const previewFormat = params.formats.includes(previewFormatPick)
    ? previewFormatPick
    : params.formats[0] ?? pickerFormat;
  const [preview, setPreview] = React.useState<PreviewState>({ status: 'loading' });
  const noPreviewVersion = noVersion && !previewVersionAt;
  const failedCards = React.useMemo(
    (): Record<string, PreviewState> => Object.fromEntries(candidates.map((tpl) => [tpl.id, { status: 'error' }])),
    [candidates],
  );
  // Built in the browser from the current edits when the runtime is in the registry, then swapped into the frame
  // after a short rest so typing does not restart the game on every key.
  const localDoc = React.useMemo<string | null>(() => {
    if (!runtime || showPicker || !template || isBanner) return null;
    return buildPlayablePreviewDoc(runtime, template.id, previewFormat, lockedParams, selected?.assets);
  }, [runtime, showPicker, template, isBanner, previewFormat, lockedParams, selected?.assets]);
  // An HTML5 banner shows every chosen size at once, each over its own plate, rested the same way.
  const localSizeDocs = React.useMemo<BannerSizeDoc[] | null>(() => {
    if (!runtime || showPicker || !template || !isBanner) return null;
    return lockedParams.formats.map((format) => ({
      format,
      html: buildPlayablePreviewDoc(runtime, template.id, format, lockedParams, selected?.assets),
    }));
  }, [runtime, showPicker, template, isBanner, lockedParams, selected?.assets]);
  const [shownSizeDocs, setShownSizeDocs] = React.useState<BannerSizeDoc[] | null>(null);
  // Rests the rebuilt documents on a timer (an external clock) before they replace the ones on screen.
  React.useEffect(() => {
    if (localSizeDocs === null) return undefined;
    const timer = window.setTimeout(() => setShownSizeDocs(localSizeDocs), LOCAL_PREVIEW_REST_MS);
    return () => window.clearTimeout(timer);
  }, [localSizeDocs]);
  // The sizes on screen: the rested documents for the chosen sizes (a size added since shows as building).
  const sizeDocs: BannerSizeDoc[] = params.formats.map(
    (format) => shownSizeDocs?.find((d) => d.format === format) ?? { format, html: '' },
  );
  const [shownDoc, setShownDoc] = React.useState<string | null>(null);
  // Rests the rebuilt document on a timer (an external clock) before it replaces the one on screen.
  React.useEffect(() => {
    if (localDoc === null) return undefined;
    const timer = window.setTimeout(() => setShownDoc(localDoc), LOCAL_PREVIEW_REST_MS);
    return () => window.clearTimeout(timer);
  }, [localDoc]);
  const livePreview: PreviewState = !runtime
    ? noPreviewVersion
      ? { status: 'error' }
      : preview
    : localDoc === null
      ? { status: 'unavailable' }
      : shownDoc
        ? { status: 'ready', html: shownDoc }
        : { status: 'loading' };
  React.useEffect(() => {
    if (runtime || showPicker || !template || !tenantId || !canPreview || !previewVersionAt || !previewVersionKey)
      return;
    const controller = new AbortController();
    setPreview((cur) => (cur.status === 'ready' ? cur : { status: 'loading' }));
    const timer = window.setTimeout(() => {
      void persistedOf(previewVersionKey).then(async (ok) => {
        if (controller.signal.aborted) return;
        if (!ok) {
          setPreview({ status: 'error' });
          return;
        }
        const res = await previewPlayable(
          tenantId,
          playable.id,
          { version_at: previewVersionAt, format: previewFormat, params: lockedParams },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        if (res.ok) setPreview({ status: 'ready', html: res.data.html });
        else if (!res.aborted) setPreview({ status: res.status === 409 ? 'unavailable' : 'error' });
      });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    runtime,
    showPicker,
    template,
    tenantId,
    canPreview,
    previewVersionAt,
    previewVersionKey,
    previewFormat,
    lockedParams,
    artSignature,
    persistedOf,
    playable.id,
  ]);

  /* ---------- export ---------- */

  const selectedTemplate = selected ? templateOf(selected.params.template_id) : undefined;
  // An unlocked version is judged against the working document (a size added since its fork has no art yet).
  const generated = playableVersionGenerated(
    selected,
    selectedTemplate,
    selected && !isLocked(selected) ? params : undefined,
  );
  const [exporting, setExporting] = React.useState(false);
  const [outcome, setOutcome] = React.useState<PlayableExportOutcome | null>(null);
  // The ZIP exported per version this session (offered again beside the tracking link).
  const [zipUrls, setZipUrls] = React.useState<Record<string, string>>({});
  const gameName = name.trim() || playable.name || (isBanner ? t('Untitled HTML5 banner') : t('Untitled mini-game'));
  const zipName = (url: string, profile?: string): string =>
    assetFilename(
      brandName,
      `${gameName} ${isBanner ? 'html5 banner' : 'mini-game'}${profile ? ` ${profile}` : ''}`,
      url,
      'zip',
    );

  // The tracking link shown for each version (by key). The export and the hosted embed click through that link (the
  // casino or a landing page, with the link's aff_id), so the ad sends its traffic where the link goes; with no link
  // they keep their own click-through (the casino).
  const [trackingLinks, setTrackingLinks] = React.useState<Record<string, TrackingLinkState | null>>({});
  /** The click-through override of a version's package: its tracking link. */
  const landingOverride = (key: string): { landing_url?: string } => {
    const link = trackingLinks[key];
    const url = link?.trackingUrl || (link?.landingPage ? link.landingUrl : undefined);
    return url ? { landing_url: url } : {};
  };

  const [embedding, setEmbedding] = React.useState(false);
  // The embed published per version this session, and the last publish failure (each shown on its own version).
  const [embeds, setEmbeds] = React.useState<Record<string, PublishedEmbed>>({});
  const [embedError, setEmbedError] = React.useState<{ key: string; text: string } | null>(null);

  /** Whether a version can be packaged with its words in the ad's language: a draft once it holds the subject's copy
   *  in it (the copy is still on its way otherwise); a locked version as it is (nothing can be filled into it). */
  const packageReadyFor = (sel: PlayableVersion & { key: string }): boolean => {
    if (isLocked(sel)) return true;
    const doc = paramsRef.current;
    return copyReadyIn(doc.localized_copy, doc.language, doc.subject.id);
  };
  const packageReady = Boolean(selected && isLocked(selected)) || copyReady;

  /** The version a package (an export or a hosted embed) is built from, with the document it packages. The package
   *  reads the VERSION's own document, so a version that took edits after it was made (a fork that kept its art) is
   *  snapshotted first and the package is exactly what the preview shows. Null (the owner told) when the edits or
   *  the snapshot could not be saved. */
  const packageTarget = async (
    sel: PlayableVersion & { key: string },
  ): Promise<{ at: string; key: string; doc: PlayableParams } | null> => {
    // The document the owner asked to package (the selected version's), taken before any wait.
    const asked = isLocked(sel) ? sel.params : paramsRef.current;
    // A fork lost while it saved moves the selection back (onForkLost told the owner): nothing is packaged from, or
    // written onto, another version than the one the owner asked for.
    const moved = (): boolean => current()?.key !== sel.key;
    // The package is built from the stored version's document: a draft's is written once more first (that write's
    // result is the answer); a generated version's never changes, so nothing is waited for.
    if (!isLocked(sel)) {
      if (!(await flush()) || moved()) return null;
      // Stored as packaged: with the locked copy (a later package of this draft reads it back).
      if (!(await write({ ad_params: lockCopy(asked), ad_version_at: sel.created_at }))) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return null;
      }
      if (moved()) return null;
    }
    // The package carries the locked copy: a generated version that still holds older copy (made before the copy
    // was locked) is snapshotted with it, its art kept (copy never reaches the art).
    const doc = lockCopy(asked);
    if (!jsonEqual(sel.params, doc)) {
      const snap = fork({
        params: cloneParams(doc),
        assets: playableArtOf(sel.assets),
        ...(sel.art_style ? { art_style: { ...sel.art_style } } : {}),
      });
      if (!snap) {
        onNotify(t("Your changes couldn't be saved. Please try again."));
        return null;
      }
      // A snapshot the backend could not save was taken back (onForkLost told the owner).
      if (!(await snap.persisted)) return null;
      return { at: snap.at, key: snap.key, doc };
    }
    if (!(await persistedOf(sel.key))) {
      onNotify(t("Your changes couldn't be saved. Please try again."));
      return null;
    }
    return { at: sel.created_at, key: sel.key, doc };
  };

  const exportNow = async (profile: string): Promise<void> => {
    const sel = current();
    if (!tenantId || !sel || exporting || embedding || formLocked || !packageReadyFor(sel)) return;
    if (
      !playableVersionGenerated(sel, templateOf(sel.params.template_id), isLocked(sel) ? undefined : paramsRef.current)
    )
      return;
    setExporting(true);
    onNotify(isBanner ? t('Preparing your HTML5 banner export…') : t('Preparing your mini-game export…'));
    // The link of the version the owner asked on (a snapshot below gets a new key with no link of its own).
    const landing = landingOverride(sel.key);
    try {
      const target = await packageTarget(sel);
      if (!target) return;
      const res = await exportPlayable(tenantId, playable.id, {
        version_at: target.at,
        profile,
        formats: target.doc.formats,
        ...landing,
      });
      if (res.ok) {
        const zip = res.data.zip_url;
        setOutcome({ profile, files: res.data.files, zip_url: zip });
        if (zip) {
          setZipUrls((cur) => ({ ...cur, [target.key]: zip }));
          await downloadAsset(zip, zipName(zip, profile));
        }
        return;
      }
      if (res.aborted) return;
      onNotify(
        res.status === 409
          ? isBanner
            ? t('HTML5 banner exports are not available in this environment yet.')
            : t('Mini-game exports are not available in this environment yet.')
          : t("The export couldn't be prepared. Please try again."),
      );
    } finally {
      setExporting(false);
    }
  };

  const publishEmbed = async (format: string): Promise<void> => {
    const sel = current();
    if (!tenantId || !sel || exporting || embedding || formLocked || !packageReadyFor(sel)) return;
    if (
      !playableVersionGenerated(sel, templateOf(sel.params.template_id), isLocked(sel) ? undefined : paramsRef.current)
    )
      return;
    setEmbedding(true);
    setEmbedError(null);
    const landing = landingOverride(sel.key);
    try {
      const target = await packageTarget(sel);
      if (!target) return;
      const res = await embedPlayable(tenantId, playable.id, { version_at: target.at, format, ...landing });
      if (res.ok) {
        setEmbeds((cur) => ({ ...cur, [target.key]: { format, result: res.data } }));
        return;
      }
      if (res.aborted) return;
      setEmbedError({
        key: target.key,
        text:
          res.status === 409
            ? t('Hosted embeds are not available in this environment yet.')
            : t("The embed couldn't be published. Please try again."),
      });
    } finally {
      setEmbedding(false);
    }
  };

  /* ---------- derived view ---------- */

  const lastRunFailed = !active && job?.status === 'failed' && !isLocked(selected);
  const stopped = lastRunFailed && isCancelled(job?.error);
  const assetErrors = !active && selected?.assets.errors ? Object.keys(selected.assets.errors) : [];
  const domain = brandHost(params.brand.domain || brandDomain);
  const canGenerate =
    Boolean(tenantId && template) &&
    params.formats.length > 0 &&
    !active &&
    !submitting &&
    !filling &&
    styleRun.ready &&
    copyReady &&
    !awaitingLanguage;
  const palette = params.brand.palette ?? EMPTY_PALETTE;
  const onBrandTheme = brandMatchesTheme(params.brand, brandTheme);

  const pickerVersions: VersionPickerItem[] = versions.map((v) => ({
    key: v.key,
    createdAt: Date.parse(v.created_at),
    // A version that carries all of its art reads as generated, like a locked one.
    locked: playableVersionGenerated(v, templateOf(v.params.template_id)),
    generating: v.key === runKey,
  }));

  const art = selected?.assets;
  // A mini-game background is flagged only when every redraw still failed its check (a real issue the owner should
  // see); a check that could not run is not a finding and stays quiet.
  const plateReview = art?.plate_qa?.status === 'failed' ? plateReviewReason(art.plate_qa.reason, t) : undefined;
  const artItems: Array<{ key: string; url: string; label: string; review?: string }> = [
    ...(art?.background_url
      ? [{ key: 'background', url: art.background_url, label: 'background', review: plateReview }]
      : []),
    // An HTML5 banner's background per size, labelled with its size.
    ...Object.entries(art?.plates ?? {})
      .filter(([, url]) => Boolean(url))
      .map(([format, url]) => ({ key: `plate:${format}`, url, label: format.replace('x', '×') })),
    ...Object.entries(art?.sprites ?? {})
      .filter(([, url]) => Boolean(url))
      .map(([key, url]) => ({ key, url, label: key })),
  ];

  const frame = formatFrameSize(previewFormat);
  const frameScale = fitScale(frame.w, frame.h, STAGE_MAX_W, STAGE_MAX_H);

  const namePlaceholder = isBanner ? t('Name this HTML5 banner') : t('Name this mini-game');

  const handleClose = (): void => {
    void flush();
    onClose();
  };

  const status = active ? (
    <>
      {job?.total ? (
        <S.FootText
          text="Painting the art · {{done}} of {{total}}"
          placeholders={{ done: job.completed ?? 0, total: job.total }}
        />
      ) : (
        <S.FootText text="Starting…" />
      )}
      <S.ProgressTrack aria-hidden="true">
        <S.ProgressFill $pct={job?.total ? ((job.completed ?? 0) / job.total) * 100 : 0} />
      </S.ProgressTrack>
    </>
  ) : stopped ? (
    <S.FootText text="Stopped. Your credits were refunded." />
  ) : !copyReady && localized.status === 'error' ? (
    <S.Hint text="The promotion's text could not be prepared in this language." />
  ) : awaitingLanguage || (!copyReady && localized.status === 'loading') ? (
    <S.Hint text="Preparing the promotion's text in this language..." />
  ) : isBanner ? (
    !template ? (
      <S.Hint text="Choose a style to start." />
    ) : generated ? (
      <S.Hint text="This version has its art. Generate again for a fresh set; copy and sizes you already have never need new art." />
    ) : (
      <S.Hint text="Lumio paints a background for each size and cuts out your subject. The copy is drawn live." />
    )
  ) : !template ? (
    <S.Hint text="Choose a game to start." />
  ) : generated ? (
    <S.Hint text="This version has its art. Generate again for a fresh set; copy and settings never need new art." />
  ) : (
    <S.Hint text="Lumio paints the background and the game's sprites. Everything else is drawn live." />
  );

  /* ---------- render ---------- */

  const toggleSection = (id: RailSection): void => setOpenSection((cur) => (cur === id ? null : id));
  const subjectTitle =
    params.subject_type === 'game'
      ? t('Game')
      : params.subject_type === 'gamification'
        ? t('Gamification')
        : t('Promotion');
  // What the subject card reads, in the ad's language: an HTML5 banner's headline and offer line, a mini-game's offer
  // title.
  const shownSubject = isBanner ? html5BannerSubject(params) : miniGameSubject(params);
  const bannerCopy = kitCopyFromSubject(params.subject_type, shownSubject);
  const offerTitle = promotionOfferTitle(shownSubject);
  // A mini-game's locked text, as it is drawn (the document's own copy may predate the lock).
  const lockedCopy = lockedParams.copy;
  const unlockedLabel = lockedParams.config.unlocked_label;

  // The subject (the first section of both rails): its own copy, read-only, and why it cannot be edited.
  const subjectSection = (
    <IngredientSection
      id="playable-subject"
      title={subjectTitle}
      icon="megaphone"
      summary={params.subject.name || params.subject.headline}
      open={openSection === 'subject'}
      onToggle={() => toggleSection('subject')}
    >
      <S.SubjectCard>
        {params.subject.image_url ? <S.SubjectThumb src={params.subject.image_url} alt="" /> : null}
        <S.SubjectCopy>
          {isBanner ? (
            <>
              <S.CopyHeadline raw text={bannerCopy.headline || params.subject.name} />
              {bannerCopy.subhead ? <S.CopySub raw text={bannerCopy.subhead} /> : null}
            </>
          ) : (
            <S.CopyHeadline raw text={offerTitle || params.subject.name} />
          )}
        </S.SubjectCopy>
      </S.SubjectCard>
      {isBanner ? null : (
        // A mini-game can advertise another promotion (its offer follows); an HTML5 banner's subject is fixed.
        <S.SubjectAction>
          <Button variant="text" label={t('Change')} onClick={openPromotionPicker} disabled={railLocked} />
        </S.SubjectAction>
      )}
      {isBanner ? null : (
        // The game's fixed text, read-only: in the ad's language, its call to action the promotion's own.
        <S.GameText>
          <S.GameTextLabel text="Headline" />
          <S.GameTextValue raw text={lockedCopy.headline} />
          <S.GameTextLabel text="Instruction" />
          <S.GameTextValue raw text={lockedCopy.instruction} />
          <S.GameTextLabel text="Reveal label" />
          <S.GameTextValue raw text={lockedCopy.reveal_label} />
          {typeof unlockedLabel === 'string' && unlockedLabel ? (
            <>
              <S.GameTextLabel text="Full meter" />
              <S.GameTextValue raw text={unlockedLabel} />
            </>
          ) : null}
          <S.GameTextLabel text="Call to action" />
          <S.GameTextValue raw text={lockedCopy.cta} />
        </S.GameText>
      )}
      <S.LockNote>
        <Icon name="lock" size={12} />
        {!isBanner ? (
          <Text
            tag="span"
            text="The offer and call to action come from the promotion and the game text is fixed, all in the ad's language, so the game never promises more than the live offer."
          />
        ) : params.subject_type === 'game' ? (
          <Text tag="span" text="The copy comes from the game, so every banner matches your site." />
        ) : params.subject_type === 'gamification' ? (
          <Text tag="span" text="The copy comes from the feature, so every banner matches your site." />
        ) : (
          <Text tag="span" text="The copy comes from the promotion, so every banner matches the live offer." />
        )}
      </S.LockNote>
    </IngredientSection>
  );

  // The ad's language (both rails, right after the subject): every word the ad shows is written in it.
  const languageSection = (
    <AdLanguageSection
      id={isBanner ? 'h5-language' : 'playable-language'}
      open={openSection === 'language'}
      onToggle={() => toggleSection('language')}
      value={params.language}
      siteLanguages={siteLanguages}
      onChange={onLanguage}
      disabled={railLocked}
      copyStatus={localized.status}
      translated={(localized.copy?.translated.length ?? 0) > 0}
      onRetry={localized.retry}
    />
  );

  // The art style the next run draws in (picked when the brand has more than one) and, when it has a cast, which
  // character: both rails show them before the art direction.
  const artStyleSections = (
    <>
      <IngredientSection
        id={isBanner ? 'h5-art-style' : 'playable-art-style'}
        title={t('Art style')}
        icon="palette"
        summary={styleRun.styleSummary}
        open={openSection === 'art-style'}
        onToggle={() => toggleSection('art-style')}
      >
        <S.ArtStyleBody>
          <AdArtStyleSection
            tenantId={tenantId}
            run={styleRun}
            onPickStyle={onStylePick}
            disabled={formLocked || filling}
          />
          <AdDrawnWith artStyle={selected?.art_style} />
        </S.ArtStyleBody>
      </IngredientSection>

      {styleRun.hasCast && featuresCharacter ? (
        <IngredientSection
          id={isBanner ? 'h5-character' : 'playable-character'}
          title={t('Character')}
          icon="user"
          summary={styleRun.characterSummary(mascotPick)}
          open={openSection === 'character'}
          onToggle={() => toggleSection('character')}
        >
          <AdCharacterSection
            tenantId={tenantId}
            value={mascotPick}
            onChange={onMascotPick}
            disabled={formLocked || filling}
            cast={styleRun.cast}
          />
        </IngredientSection>
      ) : null}
    </>
  );

  const gameRail = (
    <>
      {/* The game is chosen once, before the composer (its picker), like a landing page's template: read-only here. */}
      <IngredientSection
        id="playable-game"
        title={t('Game')}
        icon="vibe"
        summary={template?.label ?? params.template_id}
        open={openSection === 'template'}
        onToggle={() => toggleSection('template')}
      >
        <S.TemplateRow>
          <S.TemplateName raw text={template?.label ?? params.template_id} />
        </S.TemplateRow>
        <S.LockNote>
          <Icon name="lock" size={12} />
          <Text tag="span" text="The game is set when the mini-game is made. Make a new mini-game to play another one." />
        </S.LockNote>
        {devDebug ? (
          <>
            <S.FillRow>
              <S.LumioBtn
                type="button"
                onClick={() => void fillWithLumio()}
                disabled={formLocked || filling || !tenantId}
              >
                {/* Lumio glyph for this AI affordance (never an approximated sparkle/star). */}
                <LumioGlyph size={13} />
                {filling ? <Text tag="span" text="Filling…" /> : <Text tag="span" text="Fill with Lumio" />}
              </S.LumioBtn>
            </S.FillRow>
            {brandTheme ? (
              <S.Hint text="Colours, button and fonts come from your website's theme. Lumio sets the game up for your subject." />
            ) : (
              <S.Hint text="Lumio picks the colours and sets the game up for your subject." />
            )}
          </>
        ) : null}
      </IngredientSection>

      {devDebug && template ? (
        <IngredientSection
          id="playable-settings"
          title={t('Game settings')}
          icon="settings"
          open={openSection === 'settings'}
          onToggle={() => toggleSection('settings')}
        >
          {/* The game's on-screen text settings are locked with the rest of its text, so the form leaves them out. */}
          <SchemaForm
            schema={withoutFreeText(template.schema)}
            value={params.config}
            onChange={setConfig}
            disabled={railLocked}
          />
        </IngredientSection>
      ) : null}

      {template ? (
        <IngredientSection
          id="playable-formats"
          title={t('Formats')}
          icon="grid"
          summary={params.formats.map((f) => (f === 'responsive' ? t('Responsive') : formatLabel(f))).join(' · ')}
          open={openSection === 'formats'}
          onToggle={() => toggleSection('formats')}
        >
          <S.CheckRow>
            {template.formats.map((format) => (
              <Checkbox
                key={format}
                checked={params.formats.includes(format)}
                onChange={(on) => toggleFormat(format, on)}
                label={format === 'responsive' ? t('Responsive') : formatLabel(format)}
                disabled={railLocked || (params.formats.length === 1 && params.formats.includes(format))}
              />
            ))}
          </S.CheckRow>
          <S.Hint text="Responsive fills the screen, for embeds and in-app placements." />
        </IngredientSection>
      ) : null}

      {artStyleSections}

      <IngredientSection
        id="playable-direction"
        title={t('Direction')}
        icon="edit"
        optional
        summary={params.art_direction.trim() || t('Optional')}
        open={openSection === 'direction'}
        onToggle={() => toggleSection('direction')}
      >
        <TextArea
          label={t('Style, mood or colours')}
          value={params.art_direction}
          onChange={(v) => patchParams({ ...paramsRef.current, art_direction: v })}
          placeholder={t('For example: neon night, deep purple and gold.')}
          rows={3}
          disabled={railLocked}
        />
      </IngredientSection>

      {devDebug ? (
        <>
          <IngredientSection
            id="playable-brand"
            title={t('Brand colours')}
            icon="palette"
            summary={onBrandTheme ? t('Your website theme') : t('Custom colours')}
            open={openSection === 'brand'}
            onToggle={() => toggleSection('brand')}
          >
            <BrandThemeFields
              palette={palette}
              themed={brandTheme !== null}
              onTheme={onBrandTheme}
              ad="game"
              locked={railLocked}
              onPalette={setPalette}
              onReset={resetToBrandTheme}
            />
          </IngredientSection>

          <IngredientSection
            id="playable-legal"
            title={t('Legal line')}
            icon="shield"
            summary={legalPresetLabel(lockedParams.legal.preset, t)}
            open={openSection === 'legal'}
            onToggle={() => toggleSection('legal')}
          >
            {/* Always on, its market's preset: the owner picks the market only. */}
            <LegalLineField
              legal={params.legal}
              catalog={catalog}
              domain={domain}
              disabled={railLocked}
              presetOnly
              hint={t('Shown on the end card as live text, so changing it never repaints the art.')}
              onChange={(legal) => patchParams({ ...paramsRef.current, legal })}
            />
          </IngredientSection>

          <PlayableLinksSection
            id="playable-links"
            landingUrl={params.landing_url}
            onLandingUrl={(v) => patchParams({ ...paramsRef.current, landing_url: v })}
            tcUrl={params.offer.tc_url}
            onTcUrl={(v) => setOffer({ tc_url: v })}
            locked={railLocked}
            open={openSection === 'links'}
            onToggle={() => toggleSection('links')}
          />
        </>
      ) : null}
    </>
  );

  const rail = isBanner ? (
    <BannerRail
      params={params}
      template={template}
      palette={palette}
      brandTheme={brandTheme}
      onBrandTheme={onBrandTheme}
      catalog={catalog}
      domain={domain}
      locked={railLocked}
      filling={filling}
      canFill={Boolean(tenantId)}
      onFill={() => void fillWithLumio()}
      edit={editParams}
      onPalette={setPalette}
      onResetTheme={resetToBrandTheme}
      onToggleFormat={toggleFormat}
      open={openSection}
      onToggle={toggleSection}
      artStyleSections={artStyleSections}
      devDebug={devDebug}
    />
  ) : (
    gameRail
  );

  // The live preview is the right pane's content from the start (it needs no art); a note stands in only where this
  // environment cannot build previews.
  const previewUnavailable =
    (registry !== null || registryFailed) && (!canPreview || livePreview.status === 'unavailable');
  const previewBlock =
    isBanner && runtime && registry ? (
      <BannerSizesStage
        sizes={sizeDocs}
        titleOf={(format) => t('HTML5 banner preview, {{size}}', { size: formatLabel(format) })}
      />
    ) : previewUnavailable ? (
      <S.Guide>
        <S.GuideIcon>
          <Icon name="eye-off" size={20} />
        </S.GuideIcon>
        <S.GuideText text="Live previews are not available in this environment yet." />
      </S.Guide>
    ) : (
      <S.PreviewStage>
        {params.formats.length > 1 ? (
          <S.ChipRow>
            {params.formats.map((format) => (
              <Chip
                key={format}
                label={format === 'responsive' ? t('Responsive') : formatLabel(format)}
                value={format}
                selected={format === previewFormat}
                onClick={(e) => {
                  const f = e.currentTarget.dataset.value;
                  if (f) setPreviewFormatPick(f);
                }}
              />
            ))}
          </S.ChipRow>
        ) : null}
        {!registry && !registryFailed ? (
          <Skeleton block height={320} radius={12} />
        ) : livePreview.status === 'ready' && livePreview.html ? (
          <PreviewFrame
            html={livePreview.html}
            w={frame.w}
            h={frame.h}
            scale={frameScale}
            title={isBanner ? t('HTML5 banner preview') : t('Mini-game preview')}
            interactive
          />
        ) : livePreview.status === 'error' ? (
          <S.PreviewNote>
            <S.NoteText text="The preview couldn't be loaded. It retries on your next edit." />
          </S.PreviewNote>
        ) : (
          <S.PreviewNote>
            <S.Spinner aria-hidden="true" />
            <S.NoteText text="Building the preview…" />
          </S.PreviewNote>
        )}
      </S.PreviewStage>
    );

  // An HTML5 banner shows every size side by side, and a narrow sheet has no room for them beside the rail (a wide
  // strip spans two cards and was clipped): it then uses the phone's tabs, Ingredients / Banners, at any width. Never
  // a mini-game, whose one preview scales to its pane.
  const tabbed = isBanner && splitW > 0 && splitW - EASY_SPLIT_BESIDE_RAIL_PX < bannerSizesMinWidth(params.formats);

  const output = (
    <>
      {lastRunFailed && !stopped ? (
        <Callout
          tone="danger"
          title={
            isBanner
              ? t("This HTML5 banner's art couldn't be generated")
              : t("This mini-game's art couldn't be generated")
          }
          text={job?.error || t('Your credits were refunded. Please try again.')}
        />
      ) : null}
      {assetErrors.length > 0 ? (
        <Callout
          tone="warning"
          text={t('Some art could not be generated and its credits were refunded. Generate again to retry it.')}
        />
      ) : null}

      {previewBlock}
      {active ? <S.NoteText text="The art appears in the preview as it lands." /> : null}

      {artItems.length > 0 ? (
        <S.Section>
          <S.SectionTitle text="Art" />
          <S.ArtStrip>
            {artItems.map((item) => (
              <S.ArtItem key={item.key}>
                <S.ArtThumb src={item.url} alt="" loading="lazy" />
                <S.ArtLabel raw text={item.label} />
                {item.review ? <S.ReviewBadge text="Needs review" title={item.review} /> : null}
              </S.ArtItem>
            ))}
          </S.ArtStrip>
        </S.Section>
      ) : null}

      {outcome ? (
        <ExportOutcome
          profiles={registry?.profiles ?? []}
          outcome={outcome}
          onDownload={(url) => void downloadAsset(url, zipName(url, outcome.profile))}
        />
      ) : null}

      {registry?.features.embed && !isBanner ? (
        // Shown only where the backend has hosted embeds configured (the sandbox bucket).
        <EmbedPanel
          formats={params.formats}
          ready={generated && !formLocked && packageReady}
          busy={embedding}
          blocked={exporting}
          published={selected ? embeds[selected.key] ?? null : null}
          error={embedError && selected && embedError.key === selected.key ? embedError.text : null}
          onPublish={(format) => void publishEmbed(format)}
        />
      ) : null}

      {selected && generated ? (
        <TrackingLink
          key={selected.key}
          kind={isBanner ? 'banner' : 'playable'}
          creativeId={playable.id}
          versionKey={selected.key}
          promotions={promotionsForTracking(selected.params.subject)}
          poolItems={items}
          brandDomain={brandDomain}
          adName={gameName}
          videoUrl={zipUrls[selected.key]}
          downloadName={zipUrls[selected.key] ? zipName(zipUrls[selected.key]) : undefined}
          downloadLabel={t('Download ZIP')}
          tenantId={tenantId}
          pickDestination
          subject={
            selected.params.subject.id
              ? { type: selected.params.subject_type, subject: selected.params.subject }
              : undefined
          }
          onBuyCredits={onBuyCredits}
          onLinkChange={(link) => {
            const key = selected.key;
            setTrackingLinks((cur) => (cur[key] === link ? cur : { ...cur, [key]: link }));
          }}
        />
      ) : null}
    </>
  );

  const picker = (
    <S.PickerWrap>
      <S.PickerHead>
        {isBanner ? (
          <S.PickerIntro>
            <S.SectionTitle text="Choose a style" />
            <S.Hint text="How the banner moves. Every style shows the copy of what you advertise as live text. The style stays once it is chosen." />
          </S.PickerIntro>
        ) : (
          <S.PickerIntro>
            <S.SectionTitle text="Choose a game" />
            <S.Hint text="Every game ends on your offer, never on a simulated win. The game stays once it is chosen." />
          </S.PickerIntro>
        )}
        <S.PickerActions>
          {params.template_id ? (
            <Button
              variant="text"
              label={t('Cancel')}
              onClick={() => setPickingTemplate(false)}
              disabled={railLocked}
            />
          ) : (
            <S.LumioBtn
              type="button"
              onClick={() => void fillWithLumio()}
              disabled={formLocked || filling || !tenantId || !registry}
            >
              {/* Lumio glyph for this AI affordance (never an approximated sparkle/star). */}
              <LumioGlyph size={13} />
              {filling ? <Text tag="span" text="Filling…" /> : <Text tag="span" text="Let Lumio choose" />}
            </S.LumioBtn>
          )}
        </S.PickerActions>
      </S.PickerHead>
      {registry ? (
        candidates.length > 0 ? (
          <TemplatePicker
            templates={candidates}
            selectedId={params.template_id}
            previews={localCardPreviews ?? (noPreviewVersion ? failedCards : cardPreviews)}
            canPreview={canPreview}
            previewFormat={pickerFormat}
            disabled={formLocked || filling}
            {...(isBanner ? { cardW: BANNER_CARD_W, cardH: BANNER_CARD_H, ariaLabel: t('Style') } : {})}
            onPick={pickTemplate}
          />
        ) : isBanner ? (
          <S.Hint text="No style is available for this subject yet." />
        ) : (
          <S.Hint text="No game is available for this subject yet." />
        )
      ) : registryFailed ? (
        isBanner ? (
          <S.Hint text="The styles couldn't be loaded. Close the banner and open it again to retry." />
        ) : (
          <S.Hint text="The games couldn't be loaded. Close the mini-game and open it again to retry." />
        )
      ) : (
        <Skeleton block height={320} radius={12} />
      )}
    </S.PickerWrap>
  );

  return (
    <Sheet
      onClose={handleClose}
      ariaLabel={isBanner ? t('HTML5 banner') : 'Mini-game'}
      maxWidth="1100px"
      maxHeight="100%"
      header={
        <S.Header>
          <S.HeaderLeft>
            <S.TitleWrap>
              <S.TitleInput
                ref={nameRef}
                value={name}
                size={Math.max((name || namePlaceholder).length + 1, 4)}
                onChange={(e) => onName(e.target.value)}
                placeholder={namePlaceholder}
                aria-label={isBanner ? t('HTML5 banner name') : t('Mini-game name')}
              />
              <S.TitleEditIcon type="button" aria-label={t('Edit name')} onClick={() => nameRef.current?.focus()}>
                <Icon name="edit" size={14} />
              </S.TitleEditIcon>
            </S.TitleWrap>
            <VersionPicker
              versions={pickerVersions}
              value={selected?.key}
              onChange={onSelectVersion}
              disabled={submitting || filling || exporting || embedding}
            />
            <StatsPill onClick={() => statsSheet.open(playable.id)} />
          </S.HeaderLeft>
        </S.Header>
      }
    >
      {showPicker ? (
        picker
      ) : (
        <>
          <ComposerRailTabs
            value={railTab}
            onChange={setRailTab}
            outputLabel={isBanner ? 'Banners' : 'Mini-games'}
            always={tabbed}
          />
          <S.Split ref={setSplitEl} $mobileTab={railTab} $railEnter={railEnter} $tabbed={tabbed}>
            {/* Left: the ingredients (one open at a time, like the Art Style page); Generate pinned to the bottom. */}
            <S.KitRail>
              <S.RailScroll>
                {subjectSection}
                {languageSection}
                {rail}
              </S.RailScroll>
              <S.RailFoot>
                <S.RailStatus>{status}</S.RailStatus>
                {active ? (
                  <Button
                    variant="outline"
                    icon="stop"
                    fullWidth
                    label={stopping ? t('Stopping…') : t('Stop')}
                    disabled={stopping || !tenantId}
                    onClick={() => void stop()}
                  />
                ) : (
                  <GenerateButton
                    label={t('Generate art')}
                    cost={cost}
                    disabled={!canGenerate}
                    onClick={() => {
                      setRailTab('output');
                      void startRun();
                    }}
                  />
                )}
              </S.RailFoot>
            </S.KitRail>

            {/* Right: the live preview, the art and its delivery; the export pinned to the bottom once there is art. */}
            <S.KitOutput>
              <S.OutputScroll>{output}</S.OutputScroll>
              {generated ? (
                <S.OutputFoot>
                  <ExportMenu
                    profiles={canPreview ? registry?.profiles ?? [] : []}
                    ready={generated && !formLocked && packageReady}
                    busy={exporting}
                    blocked={embedding}
                    defaultProfile={template?.default_profile ?? (isBanner ? HTML5_BANNER_PROFILE : undefined)}
                    onExport={(profile) => void exportNow(profile)}
                  />
                </S.OutputFoot>
              ) : null}
            </S.KitOutput>
          </S.Split>
        </>
      )}
      {pickingPromotion ? (
        <PoolPickerSheet
          title={t('Choose the promotion this mini-game advertises')}
          poolItems={items}
          selectedIds={promotionPick ? [promotionPick] : []}
          labelOf={promotionName}
          onToggle={(id) => setPromotionPick((cur) => (cur === id ? null : id))}
          onClose={() => setPickingPromotion(false)}
          emptyText={t('This brand has no promotions yet. Create one in All Promotions & Creatives first.')}
          hint={t('Pick one. Its offer is what the game reveals.')}
          footer={
            <FooterRow>
              <SelCount text="{{n}} selected" placeholders={{ n: promotionPick ? 1 : 0 }} />
              <FooterActions>
                <Button variant="text" label={t('Cancel')} onClick={() => setPickingPromotion(false)} />
                <Button
                  variant="primary"
                  label={t('Use this promotion')}
                  onClick={confirmPromotion}
                  disabled={!promotionPick || formLocked}
                />
              </FooterActions>
            </FooterRow>
          }
        />
      ) : null}
      {statsOpen ? (
        <AssetStatsSheet
          kind={isBanner ? 'banner' : 'playable'}
          creativeId={playable.id}
          name={name}
          onClose={statsSheet.close}
        />
      ) : null}
    </Sheet>
  );
};

export default PlayableComposer;
